import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
  EventEmitter,
  Output,
  inject,
  DestroyRef,
  HostListener,
} from '@angular/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { OneTimePaymentMethodModel } from '../../../customer/customer.model';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransactionsService } from 'app/features/transactions/services/transactions.service';
import { MatPrefix, MatSuffix } from '@angular/material/form-field';
import { NgIf } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { last, debounceTime, Subscription } from 'rxjs';
import { PaymentAccount } from '../../payment.model';
import { UserStore } from 'app/features/user/store/user.store';
import { DEBIT_NOT_ACCEPTED_MESSAGE, isDebitNotAccepted } from 'app/shared/utils/debit-card.util';
import { AchDetailsFormComponent } from '../ach-details-form/ach-details-form.component';

type OneTimePaymentType = FormGroup<OneTimePaymentControlsType>;

type OneTimePaymentControlsType = {
  account: FormControl<string | null>;
  cardHolderName: FormControl<string | null>;
  expirationDate: FormControl<string | null>;
  cvv: FormControl<string | null>;
  streetAddress: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  customerName: FormControl<string | null>;
  customerReferenceNumber: FormControl<string | null>;
  saveCustomer: FormControl<boolean | null>;
  saveToken: FormControl<boolean | null>;
  cardTrackData: FormControl<string | null>;
  skipAVS: FormControl<boolean | null>;
  cardType: FormControl<string | null>;
  cardFunding: FormControl<string | null>;
  cardBank: FormControl<string | null>;
  cardBin: FormControl<string | null>;
};

@Component({
  selector: 'app-one-time-payment',
  standalone: true,
  imports: [
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MatPrefix,
    ReactiveFormsModule,
    MatSlideToggle,
    NgxMaskDirective,
    MatIconModule,
    MatButtonModule,
    MatIcon,
    NgIf,
    MatTooltipModule,
    MatButtonToggleModule,
    AchDetailsFormComponent,
  ],
  templateUrl: './one-time-payment.component.html',
  host: {
    class: 'app-one-time-payment',
  },
  // The embedded ACH form ships a bootstrap flex grid sized for a full-width
  // modal; inside this screen's fieldset the flex rows rendered beside the
  // <legend> and got squeezed/truncated. Re-lay the form as a block-level CSS
  // grid so it flows cleanly BELOW the "Bank Account Information" heading: two
  // equal columns (Account Number pairs with Confirm Account Number on one row),
  // with full-width rows (Description) spanning both. (Inline styles are plain
  // CSS, so selectors are flat, not nested.)
  styles: [
    `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    /* The fieldset <legend> is floated left (global style) and the ACH form host
       is display:inline, so the form wrapped around the float and got shoved
       right. Make the host a block that clears the float — the form then starts
       cleanly below the heading at full width. */
    :host ::ng-deep app-ach-details-form {
      display: block !important;
      clear: both !important;
      width: 100% !important;
    }
    :host ::ng-deep app-ach-details-form .container {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    :host ::ng-deep app-ach-details-form .row {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      column-gap: 16px;
      margin: 0 !important;
    }
    :host ::ng-deep app-ach-details-form .col-12 {
      grid-column: 1 / -1;
    }
    :host ::ng-deep app-ach-details-form .col-6,
    :host ::ng-deep app-ach-details-form .col-12 {
      width: auto !important;
      max-width: none !important;
      flex: none !important;
      padding: 0 !important;
    }
    :host ::ng-deep app-ach-details-form .form-control {
      display: block !important;
      width: 100% !important;
      text-align: left !important;
    }
    :host ::ng-deep app-ach-details-form mat-form-field {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
    }
    `,
  ],
  providers: [provideNgxMask()],
})
export class OneTimePaymentComponent implements OnInit {
  @ViewChild('expirationDateInput') expirationDateInput!: ElementRef;
  @ViewChild('cvvInput') cvvInput!: ElementRef;
  @ViewChild('accountInput') accountInput!: ElementRef;
  @ViewChild('cardHolderNameInput') cardHolderNameInput!: ElementRef;
  @ViewChild('addressInput') addressInput!: ElementRef;
  @ViewChild('zipInput') zipInput!: ElementRef;
  @ViewChild('customerReferenceNumberInput') customerReferenceNumberInput!: ElementRef;

  @Input() data: { payment: OneTimePaymentMethodModel | null } = { payment: null };
  @Input() hideCustomerInfo: boolean = false;
  @Input() forceNoSave: boolean = false;

  @Output() formValidated = new EventEmitter<boolean>();
  private destroyRef = inject(DestroyRef);

  private userStore = inject(UserStore);

  /** CARD (default) or ACH — only switchable when the profile flag is on. */
  paymentType: 'CARD' | 'ACH' = 'CARD';

  /** Card-control validators captured at init so they can be restored when toggling back from ACH. */
  private readonly cardControlNames = [
    'account',
    'cardHolderName',
    'expirationDate',
    'cvv',
    'streetAddress',
    'zipCode',
  ] as const;
  private savedCardValidators: Record<string, ValidatorFn | null> = {};

  // The ACH sub-form is rendered behind *ngIf, so subscribe to its validity via a
  // ViewChild setter — it re-fires whenever the embedded component appears/disappears.
  private _achForm?: AchDetailsFormComponent;
  private achFormSub?: Subscription;
  @ViewChild(AchDetailsFormComponent) set achForm(component: AchDetailsFormComponent | undefined) {
    this._achForm = component;
    this.achFormSub?.unsubscribe();
    if (component) {
      this.achFormSub = component.form.statusChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.emitValidity());
    }
    this.emitValidity();
  }
  get achForm(): AchDetailsFormComponent | undefined {
    return this._achForm;
  }

  get allowOneTimeAch(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.allowOneTimeAch ?? false;
  }

  /** Switch the screen between one-time CARD and one-time ACH entry. */
  setPaymentType(type: 'CARD' | 'ACH'): void {
    if (type === this.paymentType) {
      return;
    }
    this.paymentType = type;
    // Toggle the card validators so `form.valid` reflects only the active mode's fields.
    this.cardControlNames.forEach((name) => {
      const control = this.form.get(name);
      if (!control) {
        return;
      }
      if (type === 'ACH') {
        control.clearValidators();
      } else {
        control.setValidators(this.savedCardValidators[name] ?? null);
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
    this.emitValidity();
  }

  /** Emits the step-advancement signal based on the active payment mode. */
  private emitValidity(): void {
    if (this.paymentType === 'ACH') {
      this.formValidated.emit(this.form.valid && !!this._achForm?.form.valid);
      return;
    }
    const skipAVSWithSaveToken = !!this.form.controls.skipAVS.value && !!this.form.controls.saveToken.value;
    this.formValidated.emit(this.form.valid && !skipAVSWithSaveToken);
  }

  /** True when card verification is pending (debounce scheduled or API call in flight). */
  get isVerificationPending(): boolean {
    return this.isVerifyingCard || !!this.verifyDebounceTimer;
  }

  /**
   * Shows the manual "Verify Card" button. Appears only after the single auto-verify has
   * already happened, whenever the current data still needs verifying (last attempt failed,
   * or a field changed since the last check). Hidden while a verification is pending.
   */
  get showVerifyCardButton(): boolean {
    return (
      this.paymentType === 'CARD' &&
      this.hasAutoVerified &&
      !this.isVerifyingCard &&
      !this.verifyDebounceTimer &&
      this.hasEnoughCardData() &&
      (!this.isCardValidated || this.dataChangedSinceLastCheck())
    );
  }

  /**
   * Single unified debounce (1000ms) for the auto-verify path. Every field change resets
   * the timer; when it elapses, checkCard('auto') decides whether an (at-most-once)
   * verification actually fires.
   */
  private scheduleAutoVerify(): void {
    if (this.verifyDebounceTimer) {
      clearTimeout(this.verifyDebounceTimer);
      this.verifyDebounceTimer = null;
    }
    // Only arm the debounce when a verification would actually fire: enough valid data is
    // present AND the single auto-verify hasn't already happened for this card. Otherwise a
    // pending timer would surface a misleading "Verifying card..." while the user is still
    // typing an incomplete card (or after the one auto-verify — where the manual Verify Card
    // button takes over).
    if (this.hasAutoVerified) return;
    if (!this.hasEnoughCardData()) return;
    this.verifyDebounceTimer = setTimeout(() => {
      this.verifyDebounceTimer = null;
      this.checkCard('auto');
    }, 1000);
  }

  /** Manual re-verification (Verify Card button) — bypasses the auto-once gate and dedup guard. */
  onVerifyCardClick(): void {
    if (this.verifyDebounceTimer) {
      clearTimeout(this.verifyDebounceTimer);
      this.verifyDebounceTimer = null;
    }
    this.checkCard('manual');
  }

  /** True when the current data differs from what was last sent for verification. */
  private dataChangedSinceLastCheck(): boolean {
    const paymentAccount = this.getPaymentAccount();
    return !(
      this.lastAVSSkipped == this.skipAVS &&
      paymentAccount.requestType == this.lastChecked.requestType &&
      paymentAccount.account == this.lastChecked.account &&
      paymentAccount.cardHolderName == this.lastChecked.cardHolderName &&
      paymentAccount.expirationDate == this.lastChecked.expirationDate &&
      paymentAccount.zipCode == this.lastChecked.zipCode &&
      paymentAccount.cvv == this.lastChecked.cvv &&
      paymentAccount.streetAddress == this.lastChecked.streetAddress
    );
  }

  @HostListener('document:keydown', ['$event'])
  handleCardSwipe(event: KeyboardEvent) {
    try {
      const cardSwipePrefix1 = '%';
      const cardSwipePrefix2 = 'B';

      const testSwipePrefix1 = 'T';
      const testSwipePrefix2 = '#';

      const cardEndPrefix1 = '?';
      const cardEndPrefix2 = 'Enter';

      if (this.cardSwipeInProgress == false) {
        if (this.lastKey === cardEndPrefix1) {
          if (event.key == cardEndPrefix2) {
            event.preventDefault();
            return;
          }
          this.lastKey = '';
        }
        // else if (event.shiftKey) {
        //   console.log('Shift key pressed, starting card swipe');
        if (this.lastKey == cardSwipePrefix1 && event.key == cardSwipePrefix2) {
          this.cardSwipeInProgress = true;
          this.cardSwipeData = '%B';
          event.preventDefault();
          // console.log('Card swipe started');
          this.lastKey = '';
        }
        if (this.lastKey == testSwipePrefix1 && event.key == testSwipePrefix2) {
          this.lastKey = '';
          console.log('Test card swipe started');
          this.processCardSwipeData(
            '%B4111111111111111^TEST CARD/VI^29121015432112345678?;4111111111111111=29121015432112345678?'
          );
          //this.processCardSwipeData('%B5466160527061749^CRUZ/PATRICIA           ^280920100000028001000000362000000?;5466160527061749=2809201280010362?');
        }
        if (event.key != 'Shift') {
          this.lastKey = event.key;
          if (this.lastKey == cardSwipePrefix1) {
            console.log('Card swipe prefix 1 detected:', this.lastKey);
            event.preventDefault();
          }
        }

        // } else {
        //   this.lastKey = '';
        // }
      } else {
        if (event.key == cardEndPrefix1) {
          if (this.cardSwipeData.indexOf('?') > 0) {
            event.preventDefault();
            this.cardSwipeData += '?';
            this.lastKey = cardEndPrefix1;
            console.log('Card swipe ended');
            this.processCardSwipeData(this.cardSwipeData);
            this.cardSwipeData = '';
            this.cardSwipeInProgress = false;
          } else {
            console.log('Card swipe in progress, key first ?:');
            event.preventDefault();
            this.cardSwipeData += event.key;
          }
        } else {
          console.log('Card swipe in progress, key:', event.key);
          if (event.key != 'Shift') {
            event.preventDefault();
            this.cardSwipeData += event.key;
          }
        }
      }
    } catch (e) {
      console.error('Error in handleCardSwipe:', e);
    }
  }

  accPrefix = '';
  cardMask = '9';
  cvvMask = 'XXX';
  zipMask = '00000';
  // cardHolderNameMask = 'S*';
  // streetAddressMask = 'S*';
  // customerNameMask = 'S*';
  // customerRefNumberMask = 'A*';
  isViewMode = false;

  isCardInvalid = false;
  isCvvInvalid = false;
  isAvsInvalid = false;
  isZipInvalid = false;
  isStreetInvalid = false;
  isOneTimePaymentInvalid = false;
  isCardValidated = false;
  isVerifyingCard = false;
  isCardSwipe = false;
  invalidMessage = '';

  /** Unified 1000ms debounce timer for the auto-verify path. */
  private verifyDebounceTimer: any = null;
  /**
   * The card auto-verifies exactly once per card. Set the first time a verification fires,
   * reset when the card number (PAN) changes so a new card can auto-verify once again.
   */
  private hasAutoVerified = false;
  private lastVerifiedPan = '';
  cardType = '';
  cardFunding = '';
  cardBank = '';
  cardBin = '';
  cardTrackData = '';
  hideCardNumber = true;
  hideCvv = true;
  lastKey = '';
  cardSwipeInProgress = false;
  cardSwipeData = '';
  skipAVS = false;
  lastAccountNumber = '';
  lastCardHolderName = '';
  lastExpirationDate = '';

  hideCustomerReferenceNumber = false;
  hideCustomerName = false;

  enforceAVSMatch = false;
  isRetailProfile = false;
  requireCVV = true;
  /** Profile `blockDebitCards`; FTE also flags a refused card with `debitNotAccepted`. */
  blockDebitCards = false;
  isDebitRefused = false;

  form: OneTimePaymentType = new FormGroup<OneTimePaymentControlsType>({
    account: new FormControl(null, [
      Validators.required,
      Validators.pattern(
        /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
      ),
      this.cardInvalidValidator.bind(this),
    ]),
    cardHolderName: new FormControl(null, [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      Validators.pattern(/^[a-zA-Z\s'-]+$/),
    ]),
    cardTrackData: new FormControl(null, []),

    expirationDate: new FormControl(null, [
      Validators.required,
      Validators.pattern(/^(0[1-9]|1[0-2])([0-9]{2})$/),
      this.expirationDateValidator,
      this.expirationDateMaxValidator,
    ]),
    cvv: new FormControl(null, [Validators.required, this.cvvInvalidValidator.bind(this)]),
    streetAddress: new FormControl(null, {
      updateOn: 'blur',
      validators: [
        this.requiredNonCardTrackValidator.bind(this),
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z0-9\s'\-#.,]+$/),
        this.streetInvalidValidator.bind(this),
      ],
    }),
    zipCode: new FormControl(null, [
      //Validators.required,
      this.requiredNonCardTrackValidator.bind(this),
      Validators.pattern(/^\d{5}$/),
      Validators.minLength(5),
      Validators.maxLength(5),

      this.zipInvalidValidator.bind(this),
    ]),
    customerName: new FormControl(null, [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      Validators.pattern(/^[a-zA-Z0-9\s'\-]+$/),
    ]),
    customerReferenceNumber: new FormControl(null, [
      Validators.maxLength(25),
      Validators.pattern(/^[a-zA-Z0-9\s'\-]+$/),
    ]),

    saveCustomer: new FormControl(false),
    saveToken: new FormControl(false),
    skipAVS: new FormControl(false),
    cardType: new FormControl(null),
    cardFunding: new FormControl(null),
    cardBank: new FormControl(null),
    cardBin: new FormControl(null),
  });

  processCardSwipeData(data: string) {
    console.log('Processing card swipe data:', data);

    const track1Pattern = /^%B(\d{13,19})\^([^^]+)\^(\d{4})/;
    const track2Pattern = /^;(\d{13,19})=(\d{4})/;

    let match = data.match(track1Pattern) || data.match(track2Pattern);
    if (match) {
      const account = match[1];
      const expDate = match[3];
      // The cardHolderName field from track data may have extra spaces due to the way names are encoded (e.g., "LAST/FIRST MIDDLE      ")
      // Split on '/', reverse, join with space, then trim and replace multiple spaces with a single space
      const cardHolderName = match[2] ? match[2].split('/').reverse().join(' ').replace(/\s+/g, ' ').trim() : '';

      this.cardTrackData = data;
      this.cardMask = 'XXXX XXXX XXXX 0000';
      const swipePatch: Record<string, any> = {
        cardTrackData: data,
        skipAVS: true,
        cardHolderName: cardHolderName,
        account: account,
      };
      if (expDate && expDate.length === 4) {
        // Format expiration date as MMYY
        const month = expDate.substring(0, 2);
        const year = expDate.substring(2, 4);
        const newExpDate = Number(month) > Number(year) ? `${year}${month}` : `${month}${year}`;
        swipePatch['expirationDate'] = newExpDate;
      }
      this.form.patchValue(swipePatch, { emitEvent: false });

      switch (account.substring(0, 1)) {
        case '6':
        case '5':
        case '4':
          this.cardMask = '9000 00XX XXXX 0000';
          this.cvvMask = 'XXX';
          break;
        case '3':
          this.cardMask = '9000 00XXXX XX000';
          this.cvvMask = 'XXXX';
          break;
        default:
          this.cardMask = '99999999999999999';
          this.cvvMask = 'XXX';
          break;
      }

      if (expDate && expDate.length === 4 && this.form.controls.expirationDate.valid == false) {
        this.invalidMessage = 'Invalid Expiration Date from Card Swipe.  Please screenshot and contact support.';
      }
      this.isCardSwipe = true;
      this.skipAVS = true; // keep instance flag in sync with the patched skipAVS control (patch above used emitEvent: false)
      this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
      this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });
      this.cvvInput.nativeElement.focus();

      const activeProfile = this.userStore.activeProfile();
      if (this.autoPopulateInvoice == true) {
        if (!this.form.controls.customerName.value || this.form.controls.customerName.value.length == 0) {
          this.form.patchValue({ customerName: cardHolderName }, { emitEvent: false });
          this.hideCustomerName = true;
        }
        if (
          !this.form.controls.customerReferenceNumber.value ||
          this.form.controls.customerReferenceNumber.value.length == 0
        ) {
          const custNumber = 'swipe-' + account.substring(account.length - 4);
          this.form.patchValue({ customerReferenceNumber: custNumber }, { emitEvent: false });
          this.hideCustomerReferenceNumber = true;
        }
      }
      this.checkCard('swipe');
      const skipAVSWithSaveToken = !!this.form.controls.skipAVS.value && !!this.form.controls.saveToken.value;
      this.formValidated.emit(this.form.valid && !skipAVSWithSaveToken);
    }
  }

  get autoPopulateInvoice(): boolean {
    const profile = this.userStore.activeProfile();
    return profile?.profileDetail?.profileAdminSettings?.autoPopulateInvoice ?? false;
  }

  expirationDateMaxValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    const maxDate = new Date(today.getFullYear() + 10, today.getMonth() + 8);
    const [month, year] = control.value.match(/.{1,2}/g)!.map(Number);
    const expirationDate = new Date(2000 + year, month - 1);

    if (expirationDate > maxDate) {
      return { expirationDateTooFar: true };
    }

    return null;
  }

  getErrors() {
    return JSON.stringify(this.form.get('streetAddress')?.errors);
  }

  cardInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isCardInvalid) {
      return { cardInvalid: true };
    }
    return null;
  }
  requiredNonCardTrackValidator(control: AbstractControl): { [key: string]: boolean } | null {
    // if (this.isCardSwipe) {
    //   return null; // Skip validation if card swipe is in progress
    // }
    const isZipControl = !!this.form && control === this.form.controls.zipCode;
    // Retail profiles must always provide a valid ZIP code - it cannot be skipped.
    const skipAllowed = this.skipAVS && !(this.isRetailProfile && isZipControl);
    if (skipAllowed) {
      return null;
    }
    if (!control.value || control.value.trim() === '') {
      return { required: true };
    }

    return null;
  }
  zipInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (control.value == null) {
      return null;
    }

    if (this.isZipInvalid) {
      return { zipInvalid: true };
    }
    return null;
  }

  streetInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.skipAVS) {
      return null;
    }
    if (this.isStreetInvalid && this.enforceAVSMatch) {
      return { streetInvalid: true };
    }
    return null;
  }

  cvvInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isCvvInvalid) {
      return { cvvInvalid: true };
    }
    return null;
  }

  expirationDateValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear() % 100;

    const [month, year] = control.value.match(/.{1,2}/g)!.map(Number);

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return { expirationDateInvalid: true };
    }

    return null;
  }

  constructor(protected transactionsService: TransactionsService) {}

  async onAccountFilled() {
    if (!this.isCardInvalid) {
      //  this.expirationDateInput.nativeElement.focus();
    }
  }

  ngOnInit(): void {
    this.enforceAVSMatch = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.forceAVSMatch || false;
    this.isRetailProfile =
      this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.isRetailProfile || false;
    this.requireCVV = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.requireCVV ?? true;
    this.blockDebitCards = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.blockDebitCards === true;

    if (!this.requireCVV) {
      this.form.controls.cvv.setValidators([this.cvvInvalidValidator.bind(this) as any]);
      this.form.controls.cvv.updateValueAndValidity({ emitEvent: false });
    }

    // Snapshot the card-control validators (post requireCVV adjustment) so the ACH/card
    // toggle can clear them in ACH mode and restore them when switching back.
    this.cardControlNames.forEach((name) => {
      this.savedCardValidators[name] = this.form.get(name)?.validator ?? null;
    });

    if (this.hideCustomerInfo) {
      // Customer is already selected by the parent flow — drop validators on
      // customer fields so the form can reach `valid` without them.
      this.form.controls.customerName.clearValidators();
      this.form.controls.customerReferenceNumber.clearValidators();
      this.form.controls.customerName.updateValueAndValidity({ emitEvent: false });
      this.form.controls.customerReferenceNumber.updateValueAndValidity({ emitEvent: false });
    }

    if (this.forceNoSave) {
      this.form.controls.saveCustomer.setValue(false, { emitEvent: false });
      this.form.controls.saveToken.setValue(false, { emitEvent: false });
      this.form.controls.saveCustomer.disable({ emitEvent: false });
      this.form.controls.saveToken.disable({ emitEvent: false });
    }

    this.form.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.scheduleAutoVerify();
      this.emitValidity();
      if (this.hideCustomerInfo) {
        // Customer fields are not shown — leave validators cleared.
        return;
      }
      if (this.form.controls.saveCustomer.value) {
        this.hideCustomerName = false;
        this.hideCustomerReferenceNumber = false;
        this.form.controls.customerReferenceNumber.setValidators([
          Validators.required,
          Validators.maxLength(25),
          Validators.pattern(/^[a-zA-Z0-9\s'\-]+$/),
        ]);
      } else {
        this.form.patchValue({ saveToken: false }, { emitEvent: false });
        this.form.controls.customerReferenceNumber.setValidators([
          Validators.maxLength(25),
          Validators.pattern(/^[a-zA-Z0-9\s'\-]+$/),
        ]);
      }
      this.form.controls.customerReferenceNumber.updateValueAndValidity({ emitEvent: false });
    });

    if (this.data?.payment) {
      this.form.patchValue({
        ...{ cvv: '-' },
        ...this.data.payment,
      });
      this.isViewMode = true;
    }
    this.form
      .get('skipAVS')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.skipAVS = value || false;
        if (value) {
          const shouldClearZip = this.isZipInvalid && !this.isRetailProfile;
          const shouldClearStreet = this.isStreetInvalid;
          if (shouldClearZip || shouldClearStreet) {
            this.form.patchValue(
              {
                ...(shouldClearZip ? { zipCode: '' } : {}),
                ...(shouldClearStreet ? { streetAddress: '' } : {}),
              },
              { emitEvent: false }
            );
          }
        }
        this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
        this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });
      });
    this.form
      .get('expirationDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (!this.cardSwipeInProgress) {
          if (this.isCardSwipe) {
            if (this.lastExpirationDate !== value) {
              this.isCardSwipe = false;
              this.skipAVS = false; // keep instance flag in sync (patch below uses emitEvent: false)
              this.form.patchValue({ cardTrackData: null, skipAVS: false }, { emitEvent: false });
              this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
              this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });
            }
          }
        }
        this.lastExpirationDate = value || '';
        if (value && value.length == 4) {
          if (this.form.controls.expirationDate.valid) {
            this.cvvInput.nativeElement.focus();
          } else {
            this.cvvInput.nativeElement.focus();
            this.expirationDateInput.nativeElement.focus();
          }
        }
      });
    this.form
      .get('account')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        try {
          // When the card number changes, allow the new card to auto-verify once again.
          const normalizedPan = (value || '').toString().replace(/\D/g, '');
          if (this.hasAutoVerified && normalizedPan !== this.lastVerifiedPan) {
            this.hasAutoVerified = false;
          }
          if (!this.cardSwipeInProgress) {
            if (this.isCardSwipe) {
              if (this.lastAccountNumber !== value) {
                this.isCardSwipe = false;
                this.skipAVS = false; // keep instance flag in sync (patch below uses emitEvent: false)
                this.form.patchValue({ cardTrackData: null, skipAVS: false }, { emitEvent: false });
                this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
                this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });
              }
            }
          }
          this.lastAccountNumber = value || '';
          if (value && value.length > 0) {
            switch (value.substring(0, 1)) {
              case '6':
              case '5':
              case '4':
                this.cardMask = '9000 00XX XXXX 0000';
                //  this.cardMask = '9000 0000 0000 0000';
                this.cvvMask = 'XXX';
                if (value.length == 16) {
                  this.onAccountFilled();
                }

                break;
              case '3':
                this.cardMask = '9000 00XXXX XX000';
                this.cvvMask = 'XXXX';
                if (value.length == 15) {
                  this.onAccountFilled();
                }

                break;
              default:
                this.cardMask = '99999999999999999';
                this.cvvMask = 'XXX';
                break;
            }
          }
        } catch (e) {
          this.cardMask = '9000 0000 0000 0000';
          this.cvvMask = 'XXX';
        }
      });

    // this.form.get('expirationDate')?.valueChanges.subscribe((value) => {
    //   if (!this.cardSwipeInProgress) {
    //     if (this.isCardSwipe) {
    //       this.isCardSwipe = false;
    //       this.form.patchValue({ cardTrackData: null });
    //       this.form.patchValue({ skipAVS: false });
    //       this.form.controls.zipCode.updateValueAndValidity();
    //       this.form.controls.streetAddress.updateValueAndValidity();
    //     }

    //   }
    //   if (this.form.controls.expirationDate.valid) {
    //     this.cvvInput.nativeElement.focus();
    //   }
    // });
    // this.form.get('cvv')?.valueChanges.subscribe((value) => {
    //   if (this.form.controls.cvv.valid) {
    //     if (!this.isCardSwipe) {
    //       this.addressInput.nativeElement.focus();
    //     } else {
    //       this.zipInput.nativeElement.focus();
    //     }
    //   }
    // });
  }

  getFormData(): OneTimePaymentMethodModel {
    const formValue = this.form.getRawValue();
    if (this.paymentType === 'ACH') {
      const ach = this._achForm?.getFormData();
      return {
        paymentType: 'ACH',
        account: ach?.account ?? '',
        abaRoute: ach?.abaRoute ?? '',
        accountHolderName: ach?.accountHolderName,
        accountHolderFirstName: ach?.accountHolderFirstName,
        accountHolderLastName: ach?.accountHolderLastName,
        accountType: ach?.accountType,
        customerName: formValue.customerName!,
        customerReferenceNumber: formValue.customerReferenceNumber!,
        saveCustomer: formValue.saveCustomer!,
        saveToken: formValue.saveToken!,
      };
    }
    if (this.isCardSwipe) {
      return {
        // cardTrackData: formValue.cardTrackData!,
        // customerName: formValue.customerName!,
        // customerReferenceNumber: formValue.customerReferenceNumber!,
        // zipCode: formValue.zipCode!,
        // streetAddress: formValue.streetAddress!,
        // cvv: formValue.cvv!,
        // saveCustomer: formValue.saveCustomer!
        paymentType: 'CARD',
        account: formValue.account!,
        //cardTrackData: formValue.cardTrackData!,
        cardHolderName: formValue.cardHolderName!,
        expirationDate: formValue.expirationDate!,
        cvv: formValue.cvv!,
        streetAddress: formValue.streetAddress!,
        zipCode: formValue.zipCode!,
        customerName: formValue.customerName!,
        customerReferenceNumber: formValue.customerReferenceNumber!,
        saveCustomer: formValue.saveCustomer!,
        saveToken: formValue.saveToken!,
        issueBank: formValue.cardBank || '',
        cardFunding: formValue.cardFunding || '',
        cardLabel: formValue.cardFunding != '' ? `${formValue.cardType} ${formValue.cardFunding}` : '',
        cardBin: formValue.cardBin || this.deriveBinFromAccount(),
      };
    } else {
      return {
        paymentType: 'CARD',
        account: formValue.account!,
        //cardTrackData: formValue.cardTrackData!,
        cardHolderName: formValue.cardHolderName!,
        expirationDate: formValue.expirationDate!,
        cvv: formValue.cvv!,
        streetAddress: formValue.streetAddress!,
        zipCode: formValue.zipCode!,
        customerName: formValue.customerName!,
        customerReferenceNumber: formValue.customerReferenceNumber!,
        saveCustomer: formValue.saveCustomer!,
        saveToken: formValue.saveToken!,
        issueBank: formValue.cardBank || '',
        cardFunding: formValue.cardFunding || '',
        cardLabel: formValue.cardFunding != '' ? `${formValue.cardType} ${formValue.cardFunding}` : '',
        cardBin: formValue.cardBin || this.deriveBinFromAccount(),
      };
    }
  }

  /** Derives a 6-digit BIN from the entered PAN, when available (one-time entry only). */
  private deriveBinFromAccount(): string {
    const account = (this.form.getRawValue().account || '').toString().replace(/\D/g, '');
    return account.length >= 6 ? account.substring(0, 6) : '';
  }

  setOneTimePaymentInvalid(isInvalid: boolean, message?: string) {
    this.isOneTimePaymentInvalid = isInvalid;
    this.invalidMessage = message || '';
    if (isInvalid) {
      this.form.controls.account.updateValueAndValidity();
      this.form.controls.cardHolderName.updateValueAndValidity();
    }
  }

  lastChecked: PaymentAccount = {
    requestType: 'ACH',
  };

  lastAVSSkipped = false;

  /**
   * True when the form holds enough valid card data to send a verification request.
   * Mirrors the per-field local guards so both checkCard() and the Verify Card button
   * gate on the same "enough data" condition.
   */
  private hasEnoughCardData(): boolean {
    if (this.paymentType === 'ACH') {
      return false;
    }
    const cardNumber = this.form.get('account')?.value || '';

    if (this.isCardSwipe) {
      if (
        this.form.controls.zipCode.hasError('required') ||
        this.form.controls.zipCode.hasError('pattern') ||
        this.form.controls.zipCode.hasError('minlength') ||
        this.form.controls.zipCode.hasError('maxlength')
      ) {
        return false;
      }
      if (
        this.form.controls.streetAddress.hasError('required') ||
        this.form.controls.streetAddress.hasError('maxlength') ||
        this.form.controls.streetAddress.hasError('pattern')
      ) {
        return false;
      }
      if (this.requireCVV && this.form.controls.cvv.hasError('required')) {
        return false;
      }
      if (this.requireCVV) {
        if (cardNumber.startsWith('4') || cardNumber.startsWith('5') || cardNumber.startsWith('6')) {
          if (this.form.get('cvv')?.value?.length != 3) {
            return false;
          }
        }
        if (cardNumber.startsWith('3')) {
          if (this.form.get('cvv')?.value?.length != 4) {
            return false;
          }
        }
      }
      return true;
    }

    if (
      !(
        this.form.get('account')?.value &&
        this.form.get('cardHolderName')?.value &&
        this.form.get('expirationDate')?.value &&
        (!this.requireCVV || this.form.get('cvv')?.value)
      )
    ) {
      return false;
    }

    if (!this.skipAVS) {
      if (!this.form.get('streetAddress')?.value) {
        return false;
      }
      if (!this.form.get('zipCode')?.value) {
        return false;
      }
    } else if (this.isRetailProfile) {
      // Retail profiles require ZIP even when AVS is skipped (street may still be skipped).
      if (!this.form.get('zipCode')?.value) {
        return false;
      }
    }

    if (cardNumber.startsWith('4') && cardNumber.length != 16) {
      return false;
    }
    if (cardNumber.startsWith('6') && cardNumber.length != 16) {
      return false;
    }
    if (cardNumber.startsWith('5') && cardNumber.length != 16) {
      return false;
    }
    if (cardNumber.startsWith('3') && cardNumber.length != 15) {
      return false;
    }

    if (
      this.form.controls.expirationDate.hasError('required') ||
      this.form.controls.expirationDate.hasError('pattern') ||
      this.form.controls.expirationDate.hasError('expirationDateInvalid') ||
      this.form.controls.expirationDate.hasError('expirationDateTooFar')
    ) {
      return false;
    }
    if (
      this.form.controls.zipCode.hasError('required') ||
      this.form.controls.zipCode.hasError('pattern') ||
      this.form.controls.zipCode.hasError('minlength') ||
      this.form.controls.zipCode.hasError('maxlength')
    ) {
      return false;
    }
    if (
      this.form.controls.streetAddress.hasError('required') ||
      this.form.controls.streetAddress.hasError('maxlength') ||
      this.form.controls.streetAddress.hasError('pattern')
    ) {
      return false;
    }
    if (
      this.form.controls.cardHolderName.hasError('required') ||
      this.form.controls.cardHolderName.hasError('minlength') ||
      this.form.controls.cardHolderName.hasError('maxlength') ||
      this.form.controls.cardHolderName.hasError('pattern')
    ) {
      return false;
    }
    if (this.requireCVV && this.form.controls.cvv.hasError('required')) {
      return false;
    }
    if (this.requireCVV) {
      if (cardNumber.startsWith('4') || cardNumber.startsWith('5') || cardNumber.startsWith('6')) {
        if (this.form.get('cvv')?.value?.length != 3) {
          return false;
        }
      }
      if (cardNumber.startsWith('3')) {
        if (this.form.get('cvv')?.value?.length != 4) {
          return false;
        }
      }
    }
    return true;
  }

  checkCard(trigger: 'auto' | 'manual' | 'swipe' = 'auto') {
    // ACH entry is validated locally by the embedded ACH form — no card backend check.
    if (this.paymentType === 'ACH') {
      return;
    }
    if (!this.hasEnoughCardData()) {
      return;
    }

    const force = trigger !== 'auto';
    const paymentAccount = this.getPaymentAccount();

    // Dedup: skip if nothing changed since the last verification (manual/swipe bypass this).
    if (
      !force &&
      this.lastAVSSkipped == this.skipAVS &&
      paymentAccount.requestType == this.lastChecked.requestType &&
      paymentAccount.account == this.lastChecked.account &&
      paymentAccount.cardHolderName == this.lastChecked.cardHolderName &&
      paymentAccount.expirationDate == this.lastChecked.expirationDate &&
      paymentAccount.zipCode == this.lastChecked.zipCode &&
      paymentAccount.cvv == this.lastChecked.cvv &&
      paymentAccount.streetAddress == this.lastChecked.streetAddress
    ) {
      return;
    }

    // Auto-once gate: the card auto-verifies at most once (per PAN). Any further
    // re-verification must be user-initiated via the Verify Card button.
    if (trigger === 'auto' && this.hasAutoVerified) {
      return;
    }

    this.hasAutoVerified = true;
    this.lastVerifiedPan = (paymentAccount.account || '').toString().replace(/\D/g, '');
    this.lastChecked = paymentAccount;
    this.lastAVSSkipped = this.skipAVS;
    this.isVerifyingCard = true;

    this.transactionsService.validatePayment({ paymentAccount: paymentAccount }).subscribe(
      (response: any) => {
        this.isVerifyingCard = false;
        this.isDebitRefused = isDebitNotAccepted(response, this.blockDebitCards);
        this.isCardInvalid = !response.validCard || this.isDebitRefused;
        this.isCvvInvalid = response.validCVV === false;
        this.isStreetInvalid = response.validAVSStreet === false;
        this.isZipInvalid = response.validAVSZip === false;
        this.isAvsInvalid = this.isStreetInvalid || this.isZipInvalid;
        this.cardType = response.cardMetadata?.cardType || '';
        this.cardFunding = response.cardMetadata?.debitCreditType || '';
        this.cardBank = response.cardMetadata?.fundingBank || '';
        this.cardBin = response.cardMetadata?.cardBin || this.deriveBinFromAccount();
        this.isCardValidated = !this.isCardInvalid && !this.isCvvInvalid && !this.isZipInvalid && !this.isStreetInvalid;

        this.form.patchValue(
          { cardType: this.cardType, cardFunding: this.cardFunding, cardBank: this.cardBank, cardBin: this.cardBin },
          { emitEvent: false }
        );
        this.form.controls.account.updateValueAndValidity({ emitEvent: false });
        this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
        this.form.controls.cvv.updateValueAndValidity({ emitEvent: false });
        this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });

        if (this.isDebitRefused) {
          this.invalidMessage = DEBIT_NOT_ACCEPTED_MESSAGE;
          this.isCardValidated = false;
        } else if (this.isCardInvalid) {
          this.invalidMessage = 'Card Verification Failed';
          this.isCardValidated = false;
        } else if (this.isCvvInvalid) {
          this.invalidMessage = 'CVV Verification Failed';
          this.isCardValidated = false;
        } else if (this.isZipInvalid) {
          this.invalidMessage = 'Address Verification Failed (ZIP)';
          this.isCardValidated = false;
        } else if (this.isStreetInvalid) {
          this.invalidMessage = 'Address Verification Failed (Street)';
          this.isCardValidated = false;
        } else {
          this.isCardValidated = true;
          this.invalidMessage = `${this.cardType} Card Verified`;
        }
        const skipAVSWithSaveToken = !!this.form.controls.skipAVS.value && !!this.form.controls.saveToken.value;
        this.formValidated.emit(this.form.valid && !skipAVSWithSaveToken);
      },
      (error: any) => {
        this.isVerifyingCard = false;
        const details = error?.data?.details || {};
        this.isCardInvalid = !details.validCard;
        this.isCvvInvalid = details.validCVV === false;
        this.isStreetInvalid = details.validAVSStreet === false;
        this.isZipInvalid = details.validAVSZip === false;
        this.isAvsInvalid = this.isStreetInvalid || this.isZipInvalid;

        this.cardType = '';
        this.cardFunding = '';
        this.cardBank = '';
        this.cardBin = '';
        this.form.patchValue(
          { cardType: this.cardType, cardFunding: this.cardFunding, cardBank: this.cardBank, cardBin: this.cardBin },
          { emitEvent: false }
        );
        this.form.controls.account.updateValueAndValidity({ emitEvent: false });
        this.form.controls.zipCode.updateValueAndValidity({ emitEvent: false });
        this.form.controls.cvv.updateValueAndValidity({ emitEvent: false });
        this.form.controls.streetAddress.updateValueAndValidity({ emitEvent: false });

        const gatewayVerbiage = error?.data?.message || '';

        if (this.isCvvInvalid) {
          this.invalidMessage = gatewayVerbiage
            ? `CVV Verification Failed — ${gatewayVerbiage}`
            : 'CVV Verification Failed';
          this.isCardValidated = false;
        } else if (this.isZipInvalid) {
          this.invalidMessage = gatewayVerbiage
            ? `Address Verification Failed (ZIP) — ${gatewayVerbiage}`
            : 'Address Verification Failed (ZIP)';
          this.isCardValidated = false;
        } else if (this.isStreetInvalid) {
          this.invalidMessage = gatewayVerbiage
            ? `Address Verification Failed (Street) — ${gatewayVerbiage}`
            : 'Address Verification Failed (Street)';
          this.isCardValidated = false;
        } else {
          this.invalidMessage = gatewayVerbiage
            ? `Card Verification Failed — ${gatewayVerbiage}`
            : 'Card Verification Failed';
          this.isCardValidated = false;
        }

        const skipAVSWithSaveToken = !!this.form.controls.skipAVS.value && !!this.form.controls.saveToken.value;
        this.formValidated.emit(this.form.valid && !skipAVSWithSaveToken);
      }
    );
  }

  getPaymentAccount(): PaymentAccount {
    // A swipe is validated/charged exactly like a manually keyed card: the swipe parses the
    // PAN, cardholder, and expiration into the form and the operator keys the CVV, so we send
    // requestType 'CARD' (not 'CARDSWIPE', which the backend rejects without raw track data).
    const requestType = 'CARD';
    const zipValue = this.form.get('zipCode')?.value || '';
    const streetValue = this.form.get('streetAddress')?.value || '';
    const cvvValue = this.form.get('cvv')?.value || '';
    const hasZip = zipValue !== '';
    const hasStreet = streetValue !== '';
    // Backend requires either a value OR an explicit ignore flag for each AVS field on CARD transactions.
    // Retail profiles must never ignore zip.
    const ignoreStreet = !hasStreet && this.skipAVS ? true : undefined;
    const ignoreZip = !hasZip && this.skipAVS && !this.isRetailProfile ? true : undefined;
    const ignoreCVV = !this.requireCVV || cvvValue === '' ? true : undefined;
    return {
      requestType: requestType,
      account: this.form.get('account')?.value || '',
      cardHolderName: this.form.get('cardHolderName')?.value || '',
      expirationDate: this.form.get('expirationDate')?.value || '',
      cvv: ignoreCVV ? undefined : cvvValue,
      zipCode: hasZip ? zipValue : undefined,
      streetAddress: hasStreet ? streetValue : undefined,
      ignoreStreet: ignoreStreet,
      ignoreZip: ignoreZip,
      ignoreCVV: ignoreCVV,
    };
  }
}
